import { useState, useRef, useEffect, type CSSProperties } from "react";
import { Pill } from "../primitives";
import type { TableColumn } from "../../lib/v3-columns";

export interface TableFilter {
  column: string;
  value: string;
}

interface FilterChipRailProps {
  filters: TableFilter[];
  columns: TableColumn[];
  onChange: (filters: TableFilter[]) => void;
}

export function FilterChipRail({ filters, columns, onChange }: FilterChipRailProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [selectedCol, setSelectedCol] = useState<string | null>(null);
  const addRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (addRef.current && !addRef.current.contains(e.target as Node)) {
        setAddOpen(false);
        setSelectedCol(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filterableColumns = columns.filter(
    (c) => c.type === "enum" || c.type === "reference",
  );

  function removeFilter(idx: number) {
    onChange(filters.filter((_, i) => i !== idx));
  }

  function addFilter(column: string, value: string) {
    onChange([...filters, { column, value }]);
    setAddOpen(false);
    setSelectedCol(null);
  }

  const rail: CSSProperties = {
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
    paddingBottom: 8,
  };

  const menu: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    minWidth: 180,
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 50,
    fontFamily: "var(--font-sans)",
  };

  const menuItem: CSSProperties = {
    display: "block",
    width: "100%",
    padding: "6px 10px",
    border: "none",
    background: "transparent",
    fontSize: 13,
    textAlign: "left",
    borderRadius: "var(--border-radius-sm)",
    color: "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  if (filters.length === 0 && filterableColumns.length === 0) return null;

  const chosenCol = selectedCol ? columns.find((c) => c.field === selectedCol) : null;

  return (
    <div style={rail}>
      {filters.map((f, idx) => (
        <Pill key={`${f.column}-${f.value}-${idx}`} variant="purple">
          {f.column}: {f.value}
          <span
            style={{ marginLeft: 4, cursor: "pointer", fontSize: 10 }}
            onClick={() => removeFilter(idx)}
          >
            <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden="true" />
          </span>
        </Pill>
      ))}
      {filterableColumns.length > 0 && (
        <div ref={addRef} style={{ position: "relative" }}>
          <Pill icon="plus" variant="gray" onClick={() => setAddOpen(!addOpen)}>
            Add filter
          </Pill>
          {addOpen && !selectedCol && (
            <div style={menu} role="menu">
              {filterableColumns.map((col) => (
                <button
                  key={col.field}
                  role="menuitem"
                  style={menuItem}
                  onClick={() => setSelectedCol(col.field)}
                >
                  {col.label}
                </button>
              ))}
            </div>
          )}
          {addOpen && selectedCol && chosenCol?.choices && (
            <div style={menu} role="menu">
              {chosenCol.choices.map((choice) => (
                <button
                  key={choice}
                  role="menuitem"
                  style={menuItem}
                  onClick={() => addFilter(selectedCol, choice)}
                >
                  {choice}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
